import classNames from 'classnames';
import { StaticImage } from 'gatsby-plugin-image';
import { forwardRef, useEffect, useRef, useState, useCallback } from 'react';

import { fireTrackEvent } from '~globals/tracking';
import CircleEmptyIcon from '~images/inline/circle-empty.inline.svg';
import CircleFilledIcon from '~images/inline/circle-filled.inline.svg';

type Props = {
    heading?: string;
    subheading?: string;
    insuranceadvisortitle?: string;
    insuranceadvisorbody?: string;
    sameplanstitle?: string;
    sameplansbody?: string;
    freeservicetitle?: string;
    freeservicebody?: string;
    backgroundsurface?: SupportedBackgroundColor;
    textColor?: SupportedTextColor;
};

type CardContent = {
    image: JSX.Element;
    title: Array<string>;
    body: string;
    index: string;
};

const WhyEhealthCard = forwardRef(function WhyEhealthCard(
    { image, title, body, index }: CardContent,
    ref: React.ForwardedRef<HTMLDivElement | null>
) {
    return (
        <div
            className={classNames(
                'py-10 px-6 relative',
                'flex flex-col items-center self-stretch',
                'bg-white text-black rounded-md text-center',
                'min-w-72 md:max-w-96 xl:min-w-96'
            )}
            ref={ref}
            data-index={index}
        >
            {image}
            <p
                className={classNames(
                    'text-24 leading-32',
                    'md:text-20 md:leading-28',
                    'lg:text-24 lg:leading-32',
                    'font-bold mb-4 mt-6',
                    'px-2 lg:px-14'
                )}
            >
                {title.map((line, i) => {
                    return i === title.length - 1 ? (
                        line
                    ) : (
                        <>
                            {line}
                            <br />
                        </>
                    );
                })}
            </p>
            <p className="text-16 lg:text-18 leading-24 lg:leading-28">{body}</p>
        </div>
    );
});

let observer: IntersectionObserver;

const WhyEhealthValueProp = ({
    heading,
    subheading,
    insuranceadvisortitle = 'Your Independent Medicare Insurance Advisor',
    insuranceadvisorbody = 'Your eHealth benefit advisor gets paid the same, no matter which Medicare Advantage plan you choose.',
    sameplanstitle = 'Same plans, at the same price',
    sameplansbody = 'Get the same plans at the same price as going directly to the insurance companies.',
    freeservicetitle = 'Free service — no fees or obligation',
    freeservicebody = 'eHealth is always a free service with no obligation to enroll.',
    backgroundsurface = 'bg-surface-100',
    textColor = 'text-default',
}: Props) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [isInitialized, setIsInitialized] = useState(false);
    const mobileContainer = useRef(null as HTMLDivElement | null);

    const handleScroll = (entries: Array<IntersectionObserverEntry>) => {
        entries.forEach((entry) => {
            if (entry.intersectionRatio > 0.5) {
                const newCurrentPage = Number((entry.target as HTMLDivElement).dataset.index);
                setCurrentPage(newCurrentPage);
            }
        });
    };

    const moveMobileContent = (nPage: number) => {
        const cardItems = 4;
        if (mobileContainer.current) {
            const gapBetweenItemPx = 32;
            const scrollWidth = mobileContainer.current.scrollWidth;
            const cardWidth = mobileContainer.current.firstElementChild?.clientWidth;
            const pageWidth = (cardWidth ?? scrollWidth / cardItems) + gapBetweenItemPx;
            const newPosition = nPage * pageWidth;

            mobileContainer.current.scrollLeft = newPosition;
        }
    };

    const trackingComponentName = 'Why eHealth';
    const trackOnDotClick = () => {
        fireTrackEvent({ page: { action: `${trackingComponentName} click: dots` } });
    };

    useEffect(() => {
        observer = new IntersectionObserver(handleScroll, {
            root: mobileContainer.current,
            threshold: [0.25, 0.5, 0.75],
        });
        setIsInitialized(true);
    }, []);

    const handleOnDotClick = useCallback((indexPage: number) => {
        moveMobileContent(indexPage);
        setCurrentPage(indexPage);
        trackOnDotClick();
    }, []);

    return (
        <section className={classNames('px-4 py-10 md:py-20', backgroundsurface)}>
            <h2
                className={classNames(
                    'mx-auto text-center font-poppins font-bold',
                    'text-surface-primary-darker text-32 md:text-40 lg:text-48 leading-40 md:leading-56 lg:leading-60'
                )}
            >
                {heading || 'Why eHealth?'}
            </h2>
            <p
                className={classNames(
                    'text-center text-link-visited md:text-black font-semibold',
                    'text-18 md:text-20 lg:text-24 leading-28 lg:leading-32',
                    'mb-8 font-poppins'
                )}
            >
                {subheading || 'Your Medicare Matchmaker'}
            </p>
            {isInitialized && (
                <div
                    className={classNames(
                        'mb-7 md:mb-12 mx-auto',
                        'flex flex-row gap-6',
                        'items-start justify-start md:justify-center',
                        'overflow-x-auto md:overflow-x-visible',
                        'md:flex-wrap lg:flex-nowrap'
                    )}
                    ref={mobileContainer}
                >
                    <WhyEhealthCard
                        image={
                            <StaticImage
                                src="./images/icon-person.png"
                                alt=""
                                width={105}
                                height={105}
                            />
                        }
                        title={[insuranceadvisortitle]}
                        body={insuranceadvisorbody}
                        index="0"
                        ref={(el) => {
                            el && observer?.observe(el);
                        }}
                    />

                    <WhyEhealthCard
                        image={
                            <StaticImage
                                src="./images/icon-check.png"
                                alt=""
                                width={105}
                                height={105}
                            />
                        }
                        title={[sameplanstitle]}
                        body={sameplansbody}
                        index="1"
                        ref={(el) => {
                            el && observer?.observe(el);
                        }}
                    />

                    <WhyEhealthCard
                        image={
                            <StaticImage
                                src="./images/icon-tag.png"
                                alt=""
                                width={105}
                                height={105}
                            />
                        }
                        title={[freeservicetitle]}
                        body={freeservicebody}
                        index="2"
                        ref={(el) => {
                            el && observer?.observe(el);
                        }}
                    />
                </div>
            )}
            <div className="flex md:hidden px-20 justify-evenly mb-12" role="group">
                {[...new Array<number>(3)].map((x, i) => {
                    return (
                        <span
                            key={i}
                            onClick={() => handleOnDotClick(i)}
                            onKeyDown={() => handleOnDotClick(i)}
                            role="button"
                            tabIndex={i}
                        >
                            {i === currentPage ? (
                                <CircleFilledIcon className="text-link-visited" />
                            ) : (
                                <CircleEmptyIcon className="text-icon-primary" />
                            )}
                        </span>
                    );
                })}
            </div>
        </section>
    );
};

export default WhyEhealthValueProp;
